<template>
  <div>
  <panel class="pt-6 mt-8">
  <div class="flex flex-wrap -mx-3 border-b">
    <div class="w-full md:w-1/3 lg:w-1/3 px-3 mb-8">
      <label for="location" class="text-sm flex flex-row mb-2">Postcode / Location</label>
      <locality-suggestion-box v-model="searchParams.q" :country="searchParams.address_country" ref="location_autocomplete" placeholder="Enter postcode / location" inputClass="w-full bg-white rounded h-10 py-0 px-2 shadow text-sm border text-grey-darkest rounded-lg" />
      <small class="block text-red-dark text-medium mt-1" v-if="errorBag.get('q')" v-text="errorBag.get('q')"></small>
    </div>
    <div class="flex flex-col mb-8 justify-end">
      <button type="button"
        class="bg-tertiary hover:border-tertiary text-white h-10 py-2 px-6 text-sm border border-grey-lightest hover:border-transparent rounded-lg mr-2"
        @click="search">
        <i class="fas fa-sync fa-fw fa-spin" v-if="loading"></i>
        <span v-else>
          <i class="fas fa-search"></i> Search
        </span>
      </button>
    </div>
  </div>
  <loader v-if="loading" class="mt-5 pt-6" />
  <div v-if="!loading && officeList.data.length > 0" class="mt-5 pt-6">
    <div class="w-full max-w-full overflow-x-auto overflow-y-hidden">
      <p v-if="licenceAreaMatches.length > 0" class="mb-6 text-2xl">Licensed Offices for <span class="font-bold">`{{ licensedText }}`</span></p>
      <div class="mb-8" v-else>
        <p class="mb-12 text-2xl">No Results found for <span class="font-bold">`{{ licensedText }}`</span></p>
        <p class="mb-24 text-3xl">This location is currently unlicenced</p>
        <p class="border-b mb-8"></p>
        <p class="mb-2 text-2xl">Offices nearby to <span class="font-bold">`{{ licensedText }}`</span></p>
        <p class="mb-2 text-base">The below offices do not hold the license for <span class="font-bold">`{{ licensedText }}`</span> but do cover areas within a 30 mile radius of that location.</p>
      </div>
      <table class="table-auto text-center w-full border-collapse">
        <thead>
        <tr class="py-4 bg-grey-lightest border-b">
          <th class="text-left px-4 py-4" style="min-width: 180px;">Office Name</th>
          <th class="px-8 py-4" style="min-width: 280px;" >Licence areas / Territories</th>
          <th class="px-4 py-4">Office Address</th>
          <th class="px-4 py-4" style="min-width: 250px;">Contact Numbers</th>
          <th class="px-12 py-4">Email</th>
          <th class="px-4 py-4">Website</th>
        </tr>
        </thead>
        <tbody class="text-sm">
        <tr
          v-for="(office, i) in filteredOfficeList"
          :key="i"
          class="border-t border-grey"
        >
        <td class="text-left text-base font-bold px-4 py-4">{{ office.name }}</td>
        <td class="px-8 py-4 text-left">
          <span
            v-for="(licenseArea, j) in office.licenseAreas"
            :key="j"
            class="inline-flex items-center justify-center px-2 py-2 rounded-lg bg-grey-lighter text-xs mr-2 mb-3"
          >
            {{ licenseArea }}
          </span>
        </td>
        <td class="text-left px-4 py-4">{{ office.address }}</td>
        <td class="px-4 py-4 text-center">
          <div class="mb-1">
            <span class="mr-3" v-if="office.sales_phone">Sales:</span>
            <span class="font-semibold" v-if="office.sales_phone">{{ office.sales_phone }}</span>
          </div>
          <div>
            <span class="mr-2" v-if="office.lettings_phone">Lettings:</span>
            <span class="font-semibold" v-if="office.lettings_phone">{{ office.lettings_phone }}</span>
          </div>
        </td>
        <td class="px-4 py-4 text-center">
          <div class="mb-1" v-if="office.sales_email">
            <a :href="`mailto:${office.sales_email}`" class="text-black underline">Sales email</a>
          </div>
          <div v-if="office.lettings_email">
            <a :href="`mailto:${office.lettings_email}`" class="text-black underline">Lettings email</a>
          </div>
        </td>
        <td class="px-4 py-4">
          <a :href="getWebsiteUrl(office.website)" target="_blank" class="text-black underline">
            <i class="fas fa-link mr-2"></i>
          </a>
        </td>
        </tr>
        </tbody>
      </table>
      <paginator :data="filteredOfficeListPaginated" @change="page" />
    </div>
  </div>
  </panel>
</div>
</template>

<script>
import { v2Api } from '@/http'
import { mapState, mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import LocalitySuggestionBox from '@/components/LocalitySuggestionBox'
import ErrorBag from '@/ErrorBag'
export default {
  components: {
    LocalitySuggestionBox
  },
  data () {
    return {
      searchParams: {
        page: 1,
        q: '',
        address_country: 'GB',
        isMembersHub: true
      },
      officeList: {
        data: []
      },
      address: null,
      loading: false,
      errorBag: new ErrorBag(),
      licensedText: null
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'propertyEnums', 'system']),
    licenceAreaMatches () {
      return this.officeList.data.filter(office => office.is_assigned === true)
    },
    nearbyOffices () {
      return this.officeList.data.filter(office => office.is_assigned === false)
    },
    filteredOfficeList () {
      return this.licenceAreaMatches.length > 0
        ? this.licenceAreaMatches
        : this.nearbyOffices
    },
    filteredOfficeListPaginated () {
      const list = this.licenceAreaMatches.length > 0 ? this.licenceAreaMatches : this.nearbyOffices
      const perPage = 10
      const page = this.searchParams.page || 1
      const total = list.length
      const paginated = list.slice((page - 1) * perPage, page * perPage)

      return {
        current_page: page,
        data: paginated,
        from: (page - 1) * perPage + 1,
        last_page: Math.ceil(total / perPage),
        per_page: perPage,
        to: Math.min(page * perPage, total),
        total
      }
    }
  },
  created () {
    this.fetchPropertyEnums()
  },
  methods: {
    ...mapActions(['fetchPropertyEnums', 'displayToastNotification']),
    search: debounce(function () {
      if (!this.searchParams.q) {
        this.errorBag.store({ errors: { 'q': ['Postcode / Location field is required'] } })
        return
      }
      this.errorBag.clear()
      this.loading = true
      this.licensedText = this.searchParams.q
      this.searchParams.network_id = this.system.networks[0].id
      v2Api.get('/office/search', {
        params: this.searchParams
      }).then((response) => {
        this.officeList = response.data
        this.loading = false
      })
    }, 500),
    page (i) {
      this.searchParams.page = i
      this.search()
    },
    getWebsiteUrl (websiteUrl) {
      if (!websiteUrl) return '#'
      return websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`
    }
  }
}
</script>
